import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  effect,
  inject,
} from '@angular/core';
import { GuidedTourService } from '../../services/tour/guided-tour.service';

interface TooltipLayout {
  top: number;
  left: number;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

@Component({
  selector: 'app-guided-tour',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './guided-tour.component.html',
  styleUrl: './guided-tour.component.scss',
})
export class GuidedTourComponent implements OnDestroy {
  readonly tourService = inject(GuidedTourService);
  private readonly cdr = inject(ChangeDetectorRef);

  highlight = { top: 0, left: 0, width: 0, height: 0 };
  tooltip: TooltipLayout = { top: 0, left: 0, placement: 'bottom' };
  anchorMissing = false;

  private repositionTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      this.tourService.isActive();
      this.tourService.currentStepIndex();
      this.tourService.repositionTick();
      this.scheduleReposition();
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.repositionTimer);
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.tourService.isActive()) {
      this.reposition();
    }
  }

  next(): void {
    this.tourService.next();
  }

  previous(): void {
    this.tourService.previous();
  }

  skip(): void {
    this.tourService.skip();
  }

  private scheduleReposition(): void {
    clearTimeout(this.repositionTimer);
    this.repositionTimer = setTimeout(() => this.reposition(), 0);
  }

  private reposition(): void {
    const step = this.tourService.currentStep();
    if (!step) {
      return;
    }

    const target = document.querySelector(`[data-tour="${step.anchor}"]`) as HTMLElement | null;
    if (!target) {
      this.anchorMissing = true;
      this.cdr.markForCheck();
      return;
    }

    this.anchorMissing = false;
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });

    const rect = target.getBoundingClientRect();
    const padding = 8;
    this.highlight = {
      top: Math.max(8, rect.top - padding),
      left: Math.max(8, rect.left - padding),
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };

    this.tooltip = this.computeTooltip(rect, step.placement ?? 'auto');
    this.cdr.markForCheck();
  }

  private computeTooltip(
    rect: DOMRect,
    preferred: 'top' | 'bottom' | 'left' | 'right' | 'auto',
  ): TooltipLayout {
    const margin = 14;
    const tooltipWidth = 320;
    const tooltipHeight = 180;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const placements: Array<'top' | 'bottom' | 'left' | 'right'> =
      preferred === 'auto' ? ['bottom', 'top', 'right', 'left'] : [preferred, 'bottom', 'top', 'right', 'left'];

    for (const placement of placements) {
      let top = 0;
      let left = 0;

      if (placement === 'bottom') {
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      } else if (placement === 'top') {
        top = rect.top - tooltipHeight - margin;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      } else if (placement === 'right') {
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + margin;
      } else {
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - margin;
      }

      left = Math.max(12, Math.min(left, viewportWidth - tooltipWidth - 12));
      top = Math.max(12, Math.min(top, viewportHeight - tooltipHeight - 12));

      return { top, left, placement };
    }

    return {
      top: Math.min(rect.bottom + margin, viewportHeight - tooltipHeight - 12),
      left: Math.max(12, rect.left),
      placement: 'bottom',
    };
  }
}
